/**
 * Authentication and authorisation types
 */

// Token response from login/register/refresh endpoints
export interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

// Extended user info from /auth/me endpoint
export interface AuthUser {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  is_active: boolean
  is_verified: boolean
  created_at: string
  role: string | null
  permissions: string[]
  is_super_admin: boolean
  // RBAC v1.2 fields
  subscription_active?: boolean
  subscription_tier?: string | null
  has_app_access?: boolean
  is_client_admin?: boolean
  is_superuser_for_app?: boolean
  // Data scope per axis (allow-by-default). A missing axis key means full
  // access; ["__NO_ACCESS__"] denies it; ["*"] is a legacy full-access alias;
  // concrete ids restrict (e.g. { IBS: ["<id>"], Systems: ["<id>"] }).
  data_scopes?: Record<string, string[]>
}

// Permission constants matching the canonical 42 OR permissions in tblpermissions.
// All values must match entries where app='operational-resilience' and is_active=true.
// Updated per OR-312 RBAC rename (Section.Page.Action format, 2026-05-14 spec).
//
// Constant names preserved for backward compatibility; values updated to the
// new section-aligned strings. Deprecated permissions (Compliance.*, Reports.*)
// are mapped to closest semantic equivalents.
export const Permission = {
  // Dashboard (rehomed under PerformanceHub)
  DASHBOARD_VIEW: 'PerformanceHub.Overview.ReadOnly',

  // IBS (Configure for Edit, PerformanceHub for ReadOnly)
  IBS_VIEW: 'PerformanceHub.IBS.ReadOnly',
  IBS_CREATE: 'ConfigureHub.IBS.Edit',
  IBS_UPDATE: 'ConfigureHub.IBS.Edit',
  IBS_DELETE: 'ConfigureHub.IBS.Edit',
  IBS_EXPORT: 'PerformanceHub.IBS.ReadOnly',
  IBS_FLOWS_VIEW: 'PerformanceHub.IBSFlows.ReadOnly',
  IBS_FLOWS_EDIT: 'ConfigureHub.IBSFlows.Edit',

  // Operational Components (Configure for Edit, PerformanceHub for ReadOnly)
  OC_MICROSERVICES_VIEW: 'PerformanceHub.Microservices.ReadOnly',
  OC_MICROSERVICES_EDIT: 'ConfigureHub.Microservices.Edit',
  OC_SYSTEMS_VIEW: 'PerformanceHub.Systems.ReadOnly',
  OC_SYSTEMS_EDIT: 'ConfigureHub.Systems.Edit',
  OC_INFRASTRUCTURE_VIEW: 'PerformanceHub.Infrastructure.ReadOnly',
  OC_INFRASTRUCTURE_EDIT: 'ConfigureHub.Infrastructure.Edit',
  OC_LOCATIONS_VIEW: 'PerformanceHub.Locations.ReadOnly',
  OC_LOCATIONS_EDIT: 'ConfigureHub.Locations.Edit',
  OC_TEAMS_VIEW: 'PerformanceHub.Teams.ReadOnly',
  OC_TEAMS_EDIT: 'ConfigureHub.Teams.Edit',
  OC_SUPPLIERS_VIEW: 'PerformanceHub.Suppliers.ReadOnly',
  OC_SUPPLIERS_EDIT: 'ConfigureHub.Suppliers.Edit',
  OC_RELATIONSHIPS_VIEW: 'PerformanceHub.Relationships.ReadOnly',
  OC_RELATIONSHIPS_EDIT: 'ConfigureHub.Relationships.Edit',
  OC_CDE_VIEW: 'PerformanceHub.CDE.ReadOnly',
  OC_CDE_EDIT: 'ConfigureHub.CDE.Edit',

  // Compliance (deprecated; compliance now embedded in IBS routes per OR-312)
  COMPLIANCE_REGULATIONS_VIEW: 'PerformanceHub.IBS.ReadOnly',
  COMPLIANCE_ASSESSMENT_VIEW: 'PerformanceHub.IBS.ReadOnly',
  COMPLIANCE_ASSESSMENT_EDIT: 'ConfigureHub.IBS.Edit',
  COMPLIANCE_MAPPINGS_VIEW: 'PerformanceHub.IBS.ReadOnly',

  // Scenarios (rehomed under TestingHub)
  SCENARIO_VIEW: 'TestingHub.Scenarios.ReadOnly',
  SCENARIO_CREATE: 'TestingHub.Scenarios.Edit',
  SCENARIO_UPDATE: 'TestingHub.Scenarios.Edit',
  SCENARIO_DELETE: 'TestingHub.Scenarios.Edit',
  // Gap-fillers: Scenario Wizard run and CMORG Library are separate gates.
  SCENARIO_WIZARD_RUN: 'TestingHub.ScenarioWizard.Run',
  CMORG_LIBRARY_VIEW: 'TestingHub.CMORGLibrary.ReadOnly',

  // Risk (rehomed under RiskIntelligence)
  RISK_VIEW: 'RiskIntelligence.Risks.ReadOnly',
  RISK_CREATE: 'RiskIntelligence.Risks.Edit',
  RISK_UPDATE: 'RiskIntelligence.Risks.Edit',
  RISK_DELETE: 'RiskIntelligence.Risks.Edit',
  RISK_ASSESS: 'RiskIntelligence.Risks.Edit',

  // Incidents (rehomed under RiskIntelligence)
  INCIDENT_VIEW: 'RiskIntelligence.Incidents.ReadOnly',
  INCIDENT_CREATE: 'RiskIntelligence.Incidents.Edit',
  INCIDENT_UPDATE: 'RiskIntelligence.Incidents.Edit',
  INCIDENT_DELETE: 'RiskIntelligence.Incidents.Edit',
  INCIDENT_RESOLVE: 'RiskIntelligence.Incidents.Edit',

  // Knowledge Hub Vault (rehomed under TestingHub).
  // Upload/Delete/Admin not in OR-312 matrix; mapped to closest semantic
  // match (Edit for upload/delete, Approve for admin).
  VAULT_READONLY: 'TestingHub.ClientVault.ReadOnly',
  VAULT_EDIT: 'TestingHub.ClientVault.Edit',
  VAULT_APPROVE: 'TestingHub.ClientVault.Approve',
  VAULT_UPLOAD: 'TestingHub.ClientVault.Edit',
  VAULT_DELETE: 'TestingHub.ClientVault.Edit',
  VAULT_ADMIN: 'TestingHub.ClientVault.Approve',

  // Admin (no renames; PerformanceData moved to PerformanceHub)
  ADMIN_VIEW_AUDIT: 'Admin.AuditLog.ReadOnly',
  ADMIN_SETTINGS_VIEW: 'Admin.Settings.ReadOnly',
  ADMIN_SETTINGS_EDIT: 'Admin.Settings.Edit',
  ADMIN_PERF_VIEW: 'PerformanceHub.DataSubmission.ReadOnly',
  ADMIN_PERF_EDIT: 'PerformanceHub.DataSubmission.Edit',
  USER_VIEW: 'Admin.ManageUsers.ReadOnly',
  USER_CREATE: 'Admin.ManageUsers.Edit',
  USER_UPDATE: 'Admin.ManageUsers.Edit',
  USER_DELETE: 'Admin.ManageUsers.Edit',
  USER_INVITE: 'Admin.ManageUsers.Edit',
  USER_MANAGE_ROLES: 'Admin.ManageRoles.Edit',
  SYSTEM_VIEW_ALL: 'Admin.ManageRoles.ReadOnly',
  SYSTEM_CONFIGURE: 'Admin.ManageRoles.Edit',

  // Configure Hub gap-filler: Configure Components landing page gate.
  // Configure Hub Overview is intentionally derived from holding both
  // IBS_CREATE/IBS_UPDATE and COMPONENTS_EDIT (no own permission).
  COMPONENTS_EDIT: 'ConfigureHub.Components.Edit',
} as const

export type PermissionType = typeof Permission[keyof typeof Permission]

// Role enum values
export const Role = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  COMPLIANCE_MANAGER: 'compliance_manager',
  RISK_MANAGER: 'risk_manager',
  OPERATIONAL_MANAGER: 'operational_manager',
  ANALYST: 'analyst',
  VIEWER: 'viewer',
  USER: 'user',
} as const

export type RoleType = typeof Role[keyof typeof Role]

// Login form
export interface LoginForm {
  email: string
  password: string
}

// Login request
export interface LoginRequest {
  email: string
  password: string
}

// Register request
export interface RegisterRequest {
  email: string
  password: string
  first_name?: string
  last_name?: string
}

// Forgot password request
export interface ForgotPasswordRequest {
  email: string
}

// Reset password request
export interface ResetPasswordRequest {
  token: string
  new_password: string
}

// Change password request
export interface ChangePasswordRequest {
  current_password: string
  new_password: string
}

// Verify email request
export interface VerifyEmailRequest {
  token: string
}

// Auth state for the store
export interface AuthState {
  user: AuthUser | null
  accessToken: string | null
  refreshToken: string | null
  isLoading: boolean
  isAuthenticated: boolean
  tokenExpiresAt: number | null
}

// Token storage keys
export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  TOKEN_EXPIRES_AT: 'token_expires_at',
} as const

// Basic user info for selection dropdowns
export interface UserBasicInfo {
  userId: string
  name: string
  email: string
}

// Response for user list endpoint
export interface UserListResponse {
  items: UserBasicInfo[]
  total: number
}
